import {defaultCallback} from './defaultCallback';
import {removeDuplicates} from './utils';

/**
 * Contiene un listado de todos los sockets abiertos, con los callbacks adjuntos y demás parámetros de configuración.
 * Esta variable es utilizada por la librería para la gestión de conexión no inteligente (como la de mercados)
 * @var Object
 * {
 *  url: {
 *      ws: Instancia de WS,
 *      functions: [funciones a ejecutar],
 *      PCRA: 5,
 *      PCRA: 5,
 *      PCRS: 5
 *  }
 * }
 */
const SocketsConnected = {};

/**
 * Contiene un listado de las conexiones inteligentes a WebSockets gestionadas por la librería. Ya que el método de comprobar
 * si existe conexiones repetidas por url no funcionaría (ya que al permitir diferentes dataTypes podrían repetirse), esta variable
 * gestiona los mercados a los que está conectado un websocket, así como los dataType individuales. Así se evitan conexiones repetidas,
 * o reorganizar las conexiones.
 * con los callback adjuntos para cada y parametros de config
 * @var Object
 * {
 *  market (LBH/url tickers/marketId): {
 *     dataTypes: [0,4],
 *     url: "wss:/ak-ak-ak/"
 *     ws: Instancia de WS,
 *     functions: [funciones a ejecutar],
 *     PCRA: 5,
 *     PCRS: 5
 *  }
 */
const MarketsSocketsConnected = {};

/**
 * Crea el socket a partir de los parámetros pasados. Indica si es una conexión inteligente de mercado (mode = 'markets')
 * o es el modo básico (mode="direct") y registra los sockets devolviendo la entrada de diccionario adaptada a lo que espera
 * la función llamante en base al modo.
 *
 * @private
 * @param {string} mode Indica qué tipo de instancia debería devolver (markets, direct, etc)
 * @param {Object} options Valores que incluyen la URL, el array de callbacks y parámetros de reconexión.
 *
 * @returns {Object} Entrada del diccionario de sockets abiertos. Si es una reconexión, reemplaza al actual.
 */
const createSocket = (mode, options) => {
    let instance = {ws: null, functions: options.functions, PCRA: options.PCRA, PCRS: options.PCRS};
    if(mode === 'markets') instance = {...instance, dataTypes: options.dataTypes, url: options.url};

    const ws = new WebSocket(options.url);
    ws.onmessage = (message) => {
        try{
            let json = JSON.parse(message.data);
            instance.functions.forEach((fn) => {
                if (typeof fn === 'function')
                    fn(json)
            });
        }catch(ex){
            console.error('JSON message was expected. Plain text was sent.' + ex.message);
        }
    };
    ws.onclose = (e) => {
        ws.close();
        if(e.code === 100) return; //<-- lo hemos cerrado nosotros
        if(e.code === 1006){
            console.error('Browser has closed the socket? Maybe security rules? No reconnections allowed');
            console.error('URL was ' + options.url);
            return;
        }

        if(options.PCRA > 0){
            options.PCRA -= 1;
            setTimeout(()=>{
                if(mode === 'markets'){
                    delete MarketsSocketsConnected[options.marketId];
                    MarketsSocketsConnected[options.marketId] = instance;
                }else if(mode === 'direct'){
                    SocketsConnected[options.url] = instance;
                }
            }, options.PCRS);
        }

    };

    instance['ws'] = ws;
    return instance;
};

/**
 * Función que gestiona las conexiones a WS de forma inteligente y reutiliza las existentes.
 * Al permitir QBitia las conexiones a mercados con diversos dataType, el método de comprobar si la URL estaba
 * abierta queda obsoleto, ya que un 0,4 !== 4,0. Eso, y que se puede abrir un nuevo dataType X y adjuntarlo al anterior.
 * Para ello, este método registra marketId y dataType para poder reutilizar las conexiones o actualizarlas a medida
 * que la página va pidiendo nuevas aperturas a un mismo mercado.
 * @param {number} marketId Identificador del mercado
 * @param {array} dataTypes Array con los tipos de datos
 * @param {function| null} cb Callback a realizar cuando llegue un mensaje o null para utilizar el por defecto.
 * @private
 */
const registerSubscriberSocketByMarket = (marketId, dataTypes, cb) => {
    if(!Array.isArray(dataTypes)) dataTypes = [dataTypes];
    const functionToAttach = (typeof cb === "function") ? cb : defaultCallback;
    if(MarketsSocketsConnected && MarketsSocketsConnected.hasOwnProperty(marketId)){
        let marketSocketInstance = MarketsSocketsConnected[marketId];
        //Ya hay una conexión abierta con este mercado.
        //Determinamos si el callback es idéntico a alguno de los callback que ya se están utilizando.
        let cbInUse = marketSocketInstance.functions.filter(fnc => fnc.toString() === functionToAttach.toString()).length > 0;

        let i = 0;
        while(i < dataTypes.length){
            const currentRequestedDataType = dataTypes[i];
            const requestedDataTypeIsIncluded = marketSocketInstance.dataTypes.indexOf(currentRequestedDataType);
            if(requestedDataTypeIsIncluded === -1){
                //En esta iteración, el dataType pedido no ha coincidido con el solicitado.
                i++;
            }else{
                //Los dataType pedido y ya incluido coinciden. Decidimos en base al cb
                if(cbInUse){
                    //dataType repetido y callback repetido. Eliminamos el dataType de la petición
                    dataTypes.splice(i,1);
                    //No avanzamos la i del bucle, dado que el array ha corrido hacia la izda.
                }else{
                    //dataType repetido pero nuevo callback. Añadir a la lista de funciones a lanzar
                    marketSocketInstance.functions.push(functionToAttach);
                    //Automáticamente, el callback pasa a estar en uso
                    cbInUse = true;
                    //Eliminamos el dataType de la petición, ya que no hay que hacer ajustes en la conexión
                    dataTypes.splice(i,1);
                    //No avanzamos la i del bucle, dado que el array ha corrido hacia la izda.
                }
            }
        }

        if(dataTypes.length === 0) return;
        //Si llegados aquí, el array dataTypes contiene nuevos dataType a los que conectarse
        //Cerramos el socket y lo volvemos a abrir con los dataType antiguos y los nuevos
        // así como con las funciones anteriores y la nueva (si no está ya incluida)
        marketSocketInstance.ws.close();
        marketSocketInstance.dataTypes = [...marketSocketInstance.dataTypes, ...dataTypes];
        if(!cbInUse) marketSocketInstance.functions = [...marketSocketInstance.functions, functionToAttach];
        const url = generateStockMarketRoute(marketId, marketSocketInstance.dataTypes);

        const newSocketInstance = createSocket('markets', {
            marketId: marketId,
            dataTypes: marketSocketInstance.dataTypes,
            url: url,
            functions: marketSocketInstance.functions,
            PCRA: 5,
            PCRS: 5000
        });

        delete MarketsSocketsConnected[marketId];
        marketSocketInstance = null;
        MarketsSocketsConnected[marketId] = newSocketInstance;

    }else{
        //Esta librería no ha conectado todavía con el mercado indicado.
        const url = generateStockMarketRoute(marketId, dataTypes);
        MarketsSocketsConnected[marketId] = createSocket('markets', {
            marketId: marketId,
            dataTypes: dataTypes,
            url: url,
            functions: [functionToAttach],
            PCRA: 5, //Intentos de reconexión
            PCRS: 5000 //Milisegundos entre reconexiones
        });
    }
};

/**
 * Determina si se ha realizado una conexión con un socket a partir de la URL estrícta.
 * Comprobará si la URL ha sido abierta con un socket y, en tal caso, si el callback es idéntico (rechazando la nueva
 * apertura) o diferente de los ya introducidos (añadiendo un nuevo CB al onmessage). Si la URL no ha sido trazada
 * anteriormente, crea una conexión con el callback establecido o por defecto y lo añade al registro de sockets abiertos.
 * @param url string URL directa de conexión. Debe incluir el prefijo
 * @param cb function que contiene el callback o null para usar el callback por defecto (esta función lo establecerá)
 */
const registerSubscriberSocketByUrl = (url, cb) => {
    const functionToCall = typeof cb === 'function' ? cb : defaultCallback;
    if(SocketsConnected && SocketsConnected.hasOwnProperty(url)){
        //Ya hay una conexión abierta a esta dirección URL.
        //Buscamos si el callback pasado está ya incluido, en tal caso, lo reemplazaremos (Generalmente es una llamada con
        // el mismo callback pero diferentes parámetros; nos quedamos con el último que será el válido).
        //Si no está incluido, lo incluimos como función independiente
        let index = -1;
        if(-1 === (index = SocketsConnected[url]['functions'].toString().indexOf(functionToCall.toString()))){
            SocketsConnected[url]['functions'].push(cb);
        }else{
            SocketsConnected[url]['functions'][index] = cb;
        }
    }else{
        //No hay conexión abierta con esta URL. Creamos el socket y definimos la función que se
        // disparará cuando llegue un mensaje (o el callback por defecto o el pasado por el usuario)
        const callback = (typeof cb === 'function') ? cb : defaultCallback;
        SocketsConnected[url] = createSocket('direct', {
            url: url,
            functions: [callback],
            PCRA: 5,     //Intentos de reconexión
            PCRS: 5000   //Tiempo entre intentos de reconexión
        });
    }
};

/**
 * Genera la ruta del socket a partir del ID del mercado y los tipos de elementos deseados (por defecto solo stock)
 * Ofusca la ruta inicial para evitar que Google lo trate como una URL
 * @param {number} marketId Identificador del mercado
 * @param {array, string, number, null} dataTypes Un array con los dataType que se desean recibir. Si se pasa int
 *  o string convertirá dicho valor en componente de un array y, finalmente, si se pasa null, solo recibirá acciones.
 * @return {string} Ruta de conexión con el WS con todos los parámetros
 */
const generateStockMarketRoute = (marketId, dataTypes) => {
    if(!dataTypes) dataTypes = [0];
    else if(typeof dataTypes !== 'object') dataTypes = [dataTypes];
    const uri = JSVAR.ws_protocol + ',##' + JSVAR.ws_uri + '#websocket#data#market#';
    return uri.replace(/,/g,':').replace(/#/g, '/') + marketId + '/' + dataTypes.join(',');
};

/**
 * Genera la ruta del socket a partir de los tickerIDs de los elementos.
 * Ofusca la ruta para evitar que Google lo trate como una URL
 * @param {Array} tickerIDs Array con los tickerId de las divisas
 * @return {string} Ruta que contiene el WS
 */
const generateTickerIdSerieRoute = (tickerIDs) => {
    const tickers = tickerIDs.join(',');
    let uriPre = JSVAR.ws_protocol + ',##' + JSVAR.ws_uri + '#websocket#data#ticker#';
    return uriPre.replace(/,/g,':').replace(/#/g,'/')+tickers;
};

/**
 * SUSCRIBE UN MERCADO
 * Se susribe a los elementos de tipo dataType o tipos dataTypes del mercado identificado como marketId
 * Si callback no está definido, se vinculará el Callback genérico de la propia librería
 * @param {string, number} marketId Identificador del mercado
 * @param {string, number, array} dataTypes Identificador del dataType o dataTypes
 * @param {function|null} cb Callback a ejecutar o null para Callback por defecto.
 */
export const subscribeByMarket = (marketId, dataTypes, cb)=>{
    const market = parseInt(marketId);
    if(![65, 67, 148, 310, 1135, 1058, 1059, 9751, 10002].includes(market)){
        throw 'Only the subscriptions to markets 65, 67, 148, 310, 1135, 1058, 1059, 9751, 10002 are available. You have tried to subscribe to market '+market;
    }
    if(!Array.isArray(dataTypes)) dataTypes = [dataTypes];
    dataTypes = removeDuplicates(dataTypes);

    try{
        return registerSubscriberSocketByMarket(market, dataTypes, cb);
    }catch(e){
        console.error('Cannot connect with streaming-market ' + marketId);
    }

};

/**
 * SUSCRIBE A TICKERID'S
 * Los tickerId's series, como las divisas (o forex), no se suscriben por mercado o índice, si no por serie de tickerId.
 * No son reutilizables salvo que dos componentes soliciten la misma cadena de tickerIds
 * @param {number, Array} ids Array con los tickerId de las divisas
 * @param {function|null} cb Callback a ejecutar o null para Callback por defecto.
 */
export const subscribeByTickerIdSeries = (ids, cb)=>{
    if(Array.isArray(ids)) ids = removeDuplicates(ids);
    else ids = [ids];
    const url = generateTickerIdSerieRoute(ids);

    try{
        return registerSubscriberSocketByUrl(url, cb);
    } catch(e) {
      console.error('Cannot connect with streaming-tickers');
    }

};

/**
 * SUSCRIBE UN ÍNDICE
 * Teniendo en cuenta que un índice I pertenece a un mercado M,
 * Se suscribe a todos los componentes del mercado M y a los índices del mercado M, entre los que está I
 * De esta forma, se evita que un stock llegue por el canal mercado y por el canal índice.
 * Si callback no está definido, se vinculará el Callback genérico de la propia librería
 * @param {string, number} indexId Identificador del índice
 * @param {function|null} cb Callback a ejecutar o null para Callback por defecto.
 */
export const subscribeByIndex = (indexId, cb)=>{
    const index = parseInt(indexId);
    if(index !== 2){
        throw 'Only the subscription to index number 2 is available. You have tried to subscribe to index '+index;
    }

    try{
        return subscribeByMarket(1058, [0,4], cb);
    }catch(e) {
        console.error('Cannot connect with streaming-index ' + indexId);
    }

};

export const socketsDebug = () => console.log(MarketsSocketsConnected, SocketsConnected);
